import { Show, Studio } from "@/types";
import { SelectedShowStudio } from "@/types/booking-new.types";
import { STATUS_IDS } from "@/lib/constants/statuses";

export function findAvailableStudio(
  studios: Studio[],
  groupSize: number,
  excludeStudioIds: number[],
): Studio | null {
  // Filter studios that have enough capacity and are not already occupied
  const availableStudios = studios.filter(
    (s) => s.capacity >= groupSize && !excludeStudioIds.includes(s.id) && s.status_id === STATUS_IDS.STUDIO_ACTIVE,
  );

  if (availableStudios.length === 0) return null;

  // Sort by capacity (ascending) to get the most suitable studio
  availableStudios.sort((a, b) => a.capacity - b.capacity);

  return availableStudios[0];
}

export function checkForOverlappingShows(
  newShow: Show,
  newTime: string,
  newStudioId: number,
  existingShows: SelectedShowStudio[],
  studios: Studio[],
  groupSize: number,
  date: Date | undefined,
): {
  hasOverlap: boolean;
  overlappingShow?: SelectedShowStudio;
  availableStudios: Studio[];
} {
  const [newHour, newMinute] = newTime.split(":").map(Number);
  const newStartMinutes = newHour * 60 + newMinute;
  const newEndMinutes = newStartMinutes + (newShow.duration_minutes || 60);

  const selectedDate = date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    : "";

  // Get the list of studios assigned to the new show
  const newShowAssignedStudioIds = new Set(
    newShow.show_studios?.map((ss) => ss.studios.id) || []
  );

  // Ensure existingShows is an array
  if (!existingShows || !Array.isArray(existingShows)) {
    return {
      hasOverlap: false,
      overlappingShow: undefined,
      // Only return studios that are assigned to newShow
      availableStudios: studios.filter(
        (s) =>
          s.capacity >= groupSize &&
          s.status_id === STATUS_IDS.STUDIO_ACTIVE &&
          newShowAssignedStudioIds.has(s.id)
      ),
    };
  }

  // Check each existing show for time overlap
  for (const existingShow of existingShows) {
    // Skip if it's a different date
    if (existingShow.date !== selectedDate) continue;

    const [existingHour, existingMinute] = existingShow.time
      .split(":")
      .map(Number);
    const existingStartMinutes = existingHour * 60 + existingMinute;
    const existingEndMinutes =
      existingStartMinutes + existingShow.duration_minutes;

    // Check for time overlap
    const hasTimeOverlap =
      (newStartMinutes >= existingStartMinutes &&
        newStartMinutes < existingEndMinutes) ||
      (newEndMinutes > existingStartMinutes &&
        newEndMinutes <= existingEndMinutes) ||
      (newStartMinutes <= existingStartMinutes &&
        newEndMinutes >= existingEndMinutes);

    if (hasTimeOverlap) {
      // Check if trying to use the same studio
      if (existingShow.studio.id === newStudioId) {
        // Find alternative studios
        const occupiedStudioIds = existingShows
          .filter((s) => {
            const [h, m] = s.time.split(":").map(Number);
            const startMin = h * 60 + m;
            const endMin = startMin + s.duration_minutes;

            // Check if there's time overlap with this show
            return (
              s.date === selectedDate &&
              ((newStartMinutes >= startMin && newStartMinutes < endMin) ||
                (newEndMinutes > startMin && newEndMinutes <= endMin) ||
                (newStartMinutes <= startMin && newEndMinutes >= endMin))
            );
          })
          .map((s) => s.studio.id);

        // Only return studios that are both available AND assigned to newShow
        const availableStudios = studios.filter(
          (s) =>
            s.capacity >= groupSize &&
            !occupiedStudioIds.includes(s.id) &&
            s.status_id === STATUS_IDS.STUDIO_ACTIVE &&
            newShowAssignedStudioIds.has(s.id)
        );

        return {
          hasOverlap: true,
          overlappingShow: existingShow,
          availableStudios,
        };
      }
    }
  }

  return {
    hasOverlap: false,
    availableStudios: [],
  };
}

/**
 * Reads the `?date=` param of the new-booking page as a local-midnight date.
 *
 * Only the leading YYYY-MM-DD is used, so it accepts a plain day
 * ("2026-10-05"), a calendar timestamp ("2026-10-05T11:00:00+02:00") and a
 * timestamp whose "+" became a space in the query string. Built from its
 * parts rather than `new Date(str)`, which reads a plain day as UTC and
 * lands on the previous day west of UTC.
 */
export function parseDateParam(param: string | null | undefined): Date | null {
  const match = param?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match.map(Number);
  const date = new Date(year, month - 1, day);
  // Reject impossible days like 2026-02-31 instead of rolling over.
  return date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}
